package top.wcpe.allincore.dsl.ir.model

import top.wcpe.allincore.dsl.ir.model.action.IrFun
import top.wcpe.allincore.dsl.ir.model.action.UiAction
import top.wcpe.allincore.dsl.ir.model.animation.UiAnimation
import top.wcpe.allincore.dsl.ir.model.hud.UiHudMeta
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap
import top.wcpe.allincore.dsl.ir.model.overlay.OverlayDescriptor
import top.wcpe.allincore.dsl.ir.model.statemachine.UiStateMachine

/**
 * IR 顶层容器：一个 screen 的完整中间表示。
 *
 * 字段：
 * - `screenId` —— screen 标识符。
 * - `stateDefaults` —— state 默认值表。
 * - `actions` —— action 名 → [UiAction] 映射，保序、不可变。
 * - `themeTokens` —— 主题 token 表，值类型限制为 String / Double / Boolean，保序、不可变。
 * - `availableThemes` —— 主题预设表，值类型同 themeTokens。
 * - `themeDirectory` —— 主题展示目录，保序、不可变；通过 [withThemeDirectory] 注入。
 * - `animations` —— animation 名 → [UiAnimation] 映射，保序、不可变。
 * - `functions` —— screen 级 fun 名 → [IrFun] 映射（C4 引入），保序、不可变。
 * - `stateMachines` —— 状态机名 → [UiStateMachine] 映射（M10 引入），保序、不可变。
 * - `placeholders` —— 占位符块表（Stage 5 引入），保序、不可变。
 * - `overlayDescriptors` —— Overlay 描述表（Stage C 引入），保序、不可变。
 * - `elementOverlays` —— 元素 id → overlay 索引列表，保序、不可变。
 * - `subscribes` —— 订阅条目列表（Stage B 引入），保序、不可变。
 * - `root` —— 根节点。
 */
class UiIr(
    screenId: String?,
    stateDefaults: UiState?,
    actions: Map<String, UiAction>?,
    themeTokens: Map<String, Any?>?,
    availableThemes: Map<String, Map<String, Any?>>?,
    animations: Map<String, UiAnimation>?,
    functions: Map<String, IrFun>?,
    stateMachines: Map<String, UiStateMachine>?,
    placeholders: Map<String, UiPlaceholderBlock>?,
    overlayDescriptors: List<OverlayDescriptor>?,
    elementOverlays: Map<String, List<Int>>?,
    subscribes: List<UiSubscribeBlock>?,
    root: UiNode?,
    hudMeta: UiHudMeta?,
    /**
     * Phase 3：intercept 路由规则；未声明时为 null（不拦截任何原版界面）。
     * 通过 [withInterceptRule] 注入。
     */
    val interceptRule: UiInterceptRule?,
) {

    val screenId: String = screenId ?: throw NullPointerException("screenId")
    val stateDefaults: UiState = stateDefaults ?: throw NullPointerException("stateDefaults")

    /**
     * session state 默认值表：跨 screen / hud 共享的同名字段。
     * 未声明 `session defaults` 时为 [UiState.empty]。通过 [withSessionStateDefaults] 注入。
     */
    var sessionStateDefaults: UiState = UiState.empty()
        private set

    /**
     * persist state 默认值表：玩家退出时落盘到本地 JSON 的字段。
     * 未声明 `persist defaults` 时为 [UiState.empty]。通过 [withPersistStateDefaults] 注入。
     */
    var persistStateDefaults: UiState = UiState.empty()
        private set

    /**
     * derived 派生值表：条目值为表达式字节，运行时包成响应式 Computed。
     * 未声明 `derived defaults` 时为 [UiDerived.empty]。通过 [withDerivedDefaults] 注入。
     */
    var derivedDefaults: UiDerived = UiDerived.empty()
        private set

    /** UI DSL 主版本；旧文件缺省为 1，v2 strict 文件为 2。 */
    var dslVersion: Int = 1
        private set

    /** UI DSL 模式；当前取值为 legacy / strict。 */
    var dslMode: String = "legacy"
        private set

    /** UI DSL v2 model 声明；legacy 文件保持空模型。 */
    var model: UiModel = UiModel.empty()
        private set

    /**
     * 界面级默认字体声明：对应 DSL 顶层 `font { ... }` 块。
     * 未声明时为 `null`。通过 [withScreenFont] 注入。
     */
    var screenFont: UiScreenFont? = null
        private set

    val actions: Map<String, UiAction>
    val themeTokens: Map<String, Any?>

    /**
     * 可用主题预设表：scheme 名 → 完整 token 表，编译期由 ThemeRegistry 全量写入。
     * 客户端运行时通过 `local.setTheme(name)` 在这些预设之间切换。值类型同 [themeTokens]。
     */
    val availableThemes: Map<String, Map<String, Any?>>

    /**
     * 主题展示目录：按编译期写入顺序保序的 [ThemeDescriptor] 列表。
     * 未声明时为空列表。通过 [withThemeDirectory] 注入。
     */
    var themeDirectory: List<ThemeDescriptor> = emptyList()
        private set

    val animations: Map<String, UiAnimation>
    val functions: Map<String, IrFun>
    val stateMachines: Map<String, UiStateMachine>
    val placeholders: Map<String, UiPlaceholderBlock>
    val overlayDescriptors: List<OverlayDescriptor>
    val elementOverlays: Map<String, List<Int>>
    val subscribes: List<UiSubscribeBlock>
    val root: UiNode = root ?: throw NullPointerException("root")

    /** HUD 顶层元数据；screen 类 IR 使用 [UiHudMeta.EMPTY]。 */
    val hudMeta: UiHudMeta = hudMeta ?: UiHudMeta.EMPTY

    init {
        val actionsNn = actions ?: throw NullPointerException("actions")
        val themeTokensNn = themeTokens ?: throw NullPointerException("themeTokens")
        val availableThemesNn = availableThemes ?: throw NullPointerException("availableThemes")
        val animationsNn = animations ?: throw NullPointerException("animations")
        val functionsNn = functions ?: throw NullPointerException("functions")
        val stateMachinesNn = stateMachines ?: throw NullPointerException("stateMachines")
        val placeholdersNn = placeholders ?: throw NullPointerException("placeholders")
        val overlayDescriptorsNn = overlayDescriptors ?: throw NullPointerException("overlayDescriptors")
        val elementOverlaysNn = elementOverlays ?: throw NullPointerException("elementOverlays")
        val subscribesNn = subscribes ?: throw NullPointerException("subscribes")

        this.actions = ReadOnlyOrderedMap(LinkedHashMap(actionsNn))
        this.themeTokens = ReadOnlyOrderedMap(copyAndValidateThemeMap(themeTokensNn, "themeTokens"))

        val copiedAvailable = LinkedHashMap<String, Map<String, Any?>>(availableThemesNn.size)
        for ((scheme0, tokens0) in availableThemesNn) {
            val scheme = scheme0 ?: throw NullPointerException("availableTheme scheme")
            val tokens = tokens0 ?: throw NullPointerException("availableTheme tokens")
            copiedAvailable[scheme] =
                ReadOnlyOrderedMap(copyAndValidateThemeMap(tokens, "availableThemes[$scheme]"))
        }
        this.availableThemes = ReadOnlyOrderedMap(copiedAvailable)

        val copiedAnimations = LinkedHashMap<String, UiAnimation>(animationsNn.size)
        for ((key, value) in animationsNn) {
            copiedAnimations[key ?: throw NullPointerException("animation key")] =
                value ?: throw NullPointerException("animation value")
        }
        this.animations = ReadOnlyOrderedMap(copiedAnimations)

        val copiedFunctions = LinkedHashMap<String, IrFun>(functionsNn.size)
        for ((key, value) in functionsNn) {
            copiedFunctions[key ?: throw NullPointerException("function key")] =
                value ?: throw NullPointerException("function value")
        }
        this.functions = ReadOnlyOrderedMap(copiedFunctions)

        val copiedStateMachines = LinkedHashMap<String, UiStateMachine>(stateMachinesNn.size)
        for ((key, value) in stateMachinesNn) {
            copiedStateMachines[key ?: throw NullPointerException("stateMachine key")] =
                value ?: throw NullPointerException("stateMachine value")
        }
        this.stateMachines = ReadOnlyOrderedMap(copiedStateMachines)

        val copiedPlaceholders = LinkedHashMap<String, UiPlaceholderBlock>(placeholdersNn.size)
        for ((key, value) in placeholdersNn) {
            copiedPlaceholders[key ?: throw NullPointerException("placeholder key")] =
                value ?: throw NullPointerException("placeholder value")
        }
        this.placeholders = ReadOnlyOrderedMap(copiedPlaceholders)

        this.overlayDescriptors = ReadOnlyList(copyOverlayDescriptors(overlayDescriptorsNn))
        this.elementOverlays = ReadOnlyOrderedMap(
            copyAndValidateElementOverlays(elementOverlaysNn, this.overlayDescriptors.size),
        )
        this.subscribes = ReadOnlyList(ArrayList(subscribesNn))
    }

    /** 仅含 HUD 元数据、不含 interceptRule 的构造重载。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        availableThemes: Map<String, Map<String, Any?>>?,
        animations: Map<String, UiAnimation>?,
        functions: Map<String, IrFun>?,
        stateMachines: Map<String, UiStateMachine>?,
        placeholders: Map<String, UiPlaceholderBlock>?,
        overlayDescriptors: List<OverlayDescriptor>?,
        elementOverlays: Map<String, List<Int>>?,
        subscribes: List<UiSubscribeBlock>?,
        root: UiNode?,
        hudMeta: UiHudMeta?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, availableThemes,
        animations, functions, stateMachines, placeholders,
        overlayDescriptors, elementOverlays, subscribes, root, hudMeta, null,
    )

    /** 不含 hudMeta / interceptRule 的构造重载。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        availableThemes: Map<String, Map<String, Any?>>?,
        animations: Map<String, UiAnimation>?,
        functions: Map<String, IrFun>?,
        stateMachines: Map<String, UiStateMachine>?,
        placeholders: Map<String, UiPlaceholderBlock>?,
        overlayDescriptors: List<OverlayDescriptor>?,
        elementOverlays: Map<String, List<Int>>?,
        subscribes: List<UiSubscribeBlock>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, availableThemes,
        animations, functions, stateMachines, placeholders,
        overlayDescriptors, elementOverlays, subscribes, root, UiHudMeta.EMPTY,
    )

    /** 兼容旧签名重载：不含 subscribes。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        availableThemes: Map<String, Map<String, Any?>>?,
        animations: Map<String, UiAnimation>?,
        functions: Map<String, IrFun>?,
        stateMachines: Map<String, UiStateMachine>?,
        placeholders: Map<String, UiPlaceholderBlock>?,
        overlayDescriptors: List<OverlayDescriptor>?,
        elementOverlays: Map<String, List<Int>>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, availableThemes,
        animations, functions, stateMachines, placeholders,
        overlayDescriptors, elementOverlays, emptyList(), root,
    )

    /** 兼容旧签名重载：不含 overlay 表。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        availableThemes: Map<String, Map<String, Any?>>?,
        animations: Map<String, UiAnimation>?,
        functions: Map<String, IrFun>?,
        stateMachines: Map<String, UiStateMachine>?,
        placeholders: Map<String, UiPlaceholderBlock>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, availableThemes,
        animations, functions, stateMachines, placeholders,
        emptyList(), emptyMap(), root,
    )

    /** 兼容旧签名重载：不含 placeholders / overlay 表。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        availableThemes: Map<String, Map<String, Any?>>?,
        animations: Map<String, UiAnimation>?,
        functions: Map<String, IrFun>?,
        stateMachines: Map<String, UiStateMachine>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, availableThemes,
        animations, functions, stateMachines, emptyMap(), root,
    )

    /** 兼容旧签名重载：不含 availableThemes。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        animations: Map<String, UiAnimation>?,
        functions: Map<String, IrFun>?,
        stateMachines: Map<String, UiStateMachine>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, emptyMap(),
        animations, functions, stateMachines, root,
    )

    /** 兼容旧签名重载：不含 stateMachines。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        animations: Map<String, UiAnimation>?,
        functions: Map<String, IrFun>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, emptyMap(),
        animations, functions, emptyMap(), root,
    )

    /** 兼容旧签名重载：不含 functions / stateMachines。 */
    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        animations: Map<String, UiAnimation>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, animations,
        emptyMap(), emptyMap(), root,
    )

    constructor(
        screenId: String?,
        stateDefaults: UiState?,
        actions: Map<String, UiAction>?,
        themeTokens: Map<String, Any?>?,
        root: UiNode?,
    ) : this(
        screenId, stateDefaults, actions, themeTokens, emptyMap(),
        emptyMap(), emptyMap(), root,
    )

    /**
     * 兼容旧签名的构造重载：不含 themeTokens / animations / functions / stateMachines。
     * 仅供 dsl-ir 内部测试代码使用。生产路径必须使用含 functions / stateMachines 的新签名。
     */
    constructor(screenId: String?, stateDefaults: UiState?, actions: Map<String, UiAction>?, root: UiNode?) : this(
        screenId, stateDefaults, actions, emptyMap(),
        emptyMap(), emptyMap(), emptyMap(), root,
    )

    /** 返回替换 overlay 表后的新实例，避免为新增 overlay 字段继续膨胀构造器重载。 */
    fun withOverlays(overlayDescriptors: List<OverlayDescriptor>?, elementOverlays: Map<String, List<Int>>?): UiIr {
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        return next
    }

    /** 返回替换主题目录后的新实例；目录按输入顺序防御复制。 */
    fun withThemeDirectory(themeDirectory: List<ThemeDescriptor>?): UiIr {
        val src = themeDirectory ?: throw NullPointerException("themeDirectory")
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        next.themeDirectory = ReadOnlyList(copyThemeDirectory(src))
        return next
    }

    /** 返回填入完整 HUD 元数据的新实例。 */
    fun withHudMeta(hudMeta: UiHudMeta?): UiIr {
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        return next
    }

    /** 兼容旧签名：返回填入 hud 顶层 anchor / layer / hide 的新实例。 */
    fun withHudMeta(hudAnchor: String?, hudLayer: String?, hudHide: List<String>?): UiIr =
        withHudMeta(UiHudMeta.legacy(hudAnchor, hudLayer, hudHide))

    /** 返回填入 session state 默认值的新实例。 */
    fun withSessionStateDefaults(sessionStateDefaults: UiState?): UiIr {
        val v = sessionStateDefaults ?: throw NullPointerException("sessionStateDefaults")
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        next.sessionStateDefaults = v
        return next
    }

    /** 返回填入 persist state 默认值的新实例。 */
    fun withPersistStateDefaults(persistStateDefaults: UiState?): UiIr {
        val v = persistStateDefaults ?: throw NullPointerException("persistStateDefaults")
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        next.persistStateDefaults = v
        return next
    }

    /** 返回填入 derived 派生值表的新实例。 */
    fun withDerivedDefaults(derivedDefaults: UiDerived?): UiIr {
        val v = derivedDefaults ?: throw NullPointerException("derivedDefaults")
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        next.derivedDefaults = v
        return next
    }

    /** 返回填入界面级默认字体的新实例（`null` 或空声明表示不设界面字体）。 */
    fun withScreenFont(screenFont: UiScreenFont?): UiIr {
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, this.interceptRule,
        )
        copyOptionalFieldsTo(next)
        next.screenFont = if (screenFont == null || screenFont.isEmpty) null else screenFont
        return next
    }

    /** 返回填入 DSL 文件头信息的新实例。 */
    fun withDslHeader(dslVersion: Int, dslMode: String?): UiIr {
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        next.dslVersion = dslVersion
        next.dslMode = if (dslMode == null || dslMode.isEmpty()) "legacy" else dslMode
        return next
    }

    /** 返回填入 DSL v2 model 声明的新实例。 */
    fun withModel(model: UiModel?): UiIr {
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, interceptRule,
        )
        copyOptionalFieldsTo(next)
        next.model = model ?: UiModel.empty()
        return next
    }

    /** Phase 3：返回填入 intercept 路由规则的新实例。 */
    fun withInterceptRule(rule: UiInterceptRule?): UiIr {
        val next = UiIr(
            screenId, stateDefaults, actions, themeTokens, availableThemes,
            animations, functions, stateMachines, placeholders,
            overlayDescriptors, elementOverlays, subscribes, root, hudMeta, rule,
        )
        copyOptionalFieldsTo(next)
        return next
    }

    private fun copyOptionalFieldsTo(next: UiIr) {
        next.sessionStateDefaults = this.sessionStateDefaults
        next.persistStateDefaults = this.persistStateDefaults
        next.derivedDefaults = this.derivedDefaults
        next.dslVersion = this.dslVersion
        next.dslMode = this.dslMode
        next.model = this.model
        next.screenFont = this.screenFont
        next.themeDirectory = this.themeDirectory
    }

    // ---- 保留原 Java getter 名（下游 codec / 平台大量按 getXxx() 调用）----
    // 上述 val / var 属性由 Kotlin 自动生成 getScreenId() / getRoot() 等访问器，Java 侧无缝调用；
    // 此处以计算属性形态补充 HUD 顶层访问器：Kotlin 侧可用 .hudAnchor 属性语法，
    // Java 侧仍生成 getHudAnchor() 等，兼容原公开 API 形状。

    /** 兼容旧 getter：HUD 顶层 anchor 声明。 */
    val hudAnchor: String? get() = hudMeta.anchor

    /** 兼容旧 getter：HUD 顶层 layer 声明。 */
    val hudLayer: String? get() = hudMeta.layer

    /** 兼容旧 getter：HUD 顶层 hide 声明。 */
    val hudHide: List<String> get() = hudMeta.hide

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiIr) return false
        return dslVersion == other.dslVersion &&
            screenId == other.screenId &&
            stateDefaults == other.stateDefaults &&
            sessionStateDefaults == other.sessionStateDefaults &&
            persistStateDefaults == other.persistStateDefaults &&
            derivedDefaults == other.derivedDefaults &&
            dslMode == other.dslMode &&
            model == other.model &&
            actions == other.actions &&
            themeTokens == other.themeTokens &&
            availableThemes == other.availableThemes &&
            themeDirectory == other.themeDirectory &&
            animations == other.animations &&
            functions == other.functions &&
            stateMachines == other.stateMachines &&
            placeholders == other.placeholders &&
            overlayDescriptors == other.overlayDescriptors &&
            elementOverlays == other.elementOverlays &&
            subscribes == other.subscribes &&
            hudMeta == other.hudMeta &&
            screenFont == other.screenFont &&
            interceptRule == other.interceptRule &&
            root == other.root
    }

    override fun hashCode(): Int {
        // 复刻 Objects.hash(...) 顺序：种子 1、31 倍累乘、null 记 0。
        var result = 1
        result = 31 * result + screenId.hashCode()
        result = 31 * result + stateDefaults.hashCode()
        result = 31 * result + sessionStateDefaults.hashCode()
        result = 31 * result + persistStateDefaults.hashCode()
        result = 31 * result + derivedDefaults.hashCode()
        result = 31 * result + dslVersion.hashCode()
        result = 31 * result + dslMode.hashCode()
        result = 31 * result + model.hashCode()
        result = 31 * result + actions.hashCode()
        result = 31 * result + themeTokens.hashCode()
        result = 31 * result + availableThemes.hashCode()
        result = 31 * result + themeDirectory.hashCode()
        result = 31 * result + animations.hashCode()
        result = 31 * result + functions.hashCode()
        result = 31 * result + stateMachines.hashCode()
        result = 31 * result + placeholders.hashCode()
        result = 31 * result + overlayDescriptors.hashCode()
        result = 31 * result + elementOverlays.hashCode()
        result = 31 * result + subscribes.hashCode()
        result = 31 * result + hudMeta.hashCode()
        result = 31 * result + (screenFont?.hashCode() ?: 0)
        result = 31 * result + (interceptRule?.hashCode() ?: 0)
        result = 31 * result + root.hashCode()
        return result
    }

    override fun toString(): String =
        "UiIr{" + screenId +
            ",state=" + stateDefaults +
            ",session=" + sessionStateDefaults +
            ",persist=" + persistStateDefaults +
            ",dsl=" + dslVersion + "/" + dslMode +
            ",model=" + model +
            ",actions=" + actions.keys +
            ",theme=" + themeTokens.keys +
            ",themeDirectory=" + themeDirectory.size +
            ",animations=" + animations.keys +
            ",functions=" + functions.keys +
            ",stateMachines=" + stateMachines.keys +
            ",placeholders=" + placeholders.keys +
            ",overlays=" + overlayDescriptors.size +
            ",elementOverlays=" + elementOverlays.keys +
            ",subscribes=" + subscribes.size +
            ",hudMeta=" + hudMeta +
            ",screenFont=" + screenFont +
            ",interceptRule=" + interceptRule +
            ",root=" + root + "}"

    private companion object {
        /** 提取的 token 表拷贝 + 类型校验逻辑，复用给 themeTokens 与 availableThemes。 */
        fun copyAndValidateThemeMap(src: Map<String, Any?>, label: String): LinkedHashMap<String, Any?> {
            val copied = LinkedHashMap<String, Any?>(src.size)
            for ((key0, value0) in src) {
                val key = key0 ?: throw NullPointerException(label + " key")
                val value = value0 ?: throw NullPointerException(label + " value")
                if (!(value is String || value is Double || value is Boolean)) {
                    throw IllegalArgumentException(
                        label + "[" + key + "] 不支持的值类型: " + value::class,
                    )
                }
                copied[key] = value
            }
            return copied
        }

        fun copyThemeDirectory(themeDirectory: List<ThemeDescriptor>): ArrayList<ThemeDescriptor> {
            val copied = ArrayList<ThemeDescriptor>(themeDirectory.size)
            for (descriptor in themeDirectory) {
                copied.add(descriptor ?: throw NullPointerException("themeDescriptor"))
            }
            return copied
        }

        fun copyOverlayDescriptors(overlayDescriptors: List<OverlayDescriptor>): ArrayList<OverlayDescriptor> {
            val copied = ArrayList<OverlayDescriptor>(overlayDescriptors.size)
            for (descriptor in overlayDescriptors) {
                copied.add(descriptor ?: throw NullPointerException("overlayDescriptor"))
            }
            return copied
        }

        fun copyAndValidateElementOverlays(
            elementOverlays: Map<String, List<Int>>,
            overlayDescriptorCount: Int,
        ): LinkedHashMap<String, List<Int>> {
            val copied = LinkedHashMap<String, List<Int>>(elementOverlays.size)
            for ((elementId0, indexes0) in elementOverlays) {
                val elementId = elementId0 ?: throw NullPointerException("elementOverlays key")
                val indexes = indexes0 ?: throw NullPointerException("elementOverlays value")
                copied[elementId] =
                    ReadOnlyList(copyOverlayIndexList(elementId, indexes, overlayDescriptorCount))
            }
            return copied
        }

        fun copyOverlayIndexList(
            elementId: String,
            indexes: List<Int>,
            overlayDescriptorCount: Int,
        ): ArrayList<Int> {
            val copied = ArrayList<Int>(indexes.size)
            for (index in indexes) {
                val value = index ?: throw NullPointerException("overlayIndex")
                if (value < 0 || value >= overlayDescriptorCount) {
                    throw IllegalArgumentException(
                        "elementOverlays[" + elementId + "] 中的 overlayIndex 越界: " + value,
                    )
                }
                copied.add(value)
            }
            return copied
        }
    }
}
